/**
 * Crops (vanilla CropBlock): wheat / carrots / potatoes / beetroots as one
 * block id per growth age, planted on farmland, grown by random ticks
 * (world/crops.ts), with vanilla loot. DOM-free.
 *
 * Growth (CropBlock.randomTick): with light >= 9 above the crop, a random
 * tick advances the age with chance 1 / (floor(25 / f) + 1), where f is the
 * growth speed (CropBlock.getGrowthSpeed): 1, plus for each farmland in the
 * 3x3 under and around the crop 1 (dry) or 3 (moist) — a quarter of that for
 * the 8 around. Random ticks hit a given block 3/4096 of game ticks.
 * (Vanilla also halves f for same-crop rows; not modelled.)
 */

import {
  BEETROOT_STAGE_IDS, CARROT_STAGE_IDS, FARMLAND_DRY_ID, FARMLAND_ID, POTATO_STAGE_IDS, WHEAT_ID, WHEAT_STAGE_IDS,
} from '../blocks';
import { I } from './itemIds';
import { stackOf, type Stack } from './types';

export type CropKind = 'wheat' | 'carrots' | 'potatoes' | 'beetroots';

export interface CropFamily {
  kind: CropKind;
  /** Block id per age; the last is mature. */
  stages: readonly number[];
  /** Item that plants it. */
  seed: number;
}

export const CROPS: Record<CropKind, CropFamily> = {
  wheat: { kind: 'wheat', stages: [...WHEAT_STAGE_IDS, WHEAT_ID], seed: I.WHEAT_SEEDS },
  carrots: { kind: 'carrots', stages: CARROT_STAGE_IDS, seed: I.CARROT },
  potatoes: { kind: 'potatoes', stages: POTATO_STAGE_IDS, seed: I.POTATO },
  beetroots: { kind: 'beetroots', stages: BEETROOT_STAGE_IDS, seed: I.BEETROOT_SEEDS },
};

const BY_BLOCK = new Map<number, { fam: CropFamily; age: number }>();
const BY_SEED = new Map<number, CropFamily>();
for (const fam of Object.values(CROPS)) {
  fam.stages.forEach((id, age) => BY_BLOCK.set(id, { fam, age }));
  BY_SEED.set(fam.seed, fam);
}

export const RANDOM_TICK_CHANCE = 3 / 4096;
export const MIN_GROW_LIGHT = 9;

export const isCrop = (id: number): boolean => BY_BLOCK.has(id);
export const cropOf = (id: number): { fam: CropFamily; age: number } | undefined => BY_BLOCK.get(id);
export const isMatureCrop = (id: number): boolean => { const c = BY_BLOCK.get(id); return !!c && c.age === c.fam.stages.length - 1; };
/** Crop block an item plants (age 0), or 0. */
export const plantedBy = (itemId: number): number => BY_SEED.get(itemId)?.stages[0] ?? 0;
/** Can a crop stand on `below`? */
export const isFarmland = (id: number): boolean => id === FARMLAND_ID || id === FARMLAND_DRY_ID;
export const canSustainCrop = (below: number): boolean => isFarmland(below);
/** Farmland's growth weight: moist 3, dry 1, other 0. */
export const farmlandWeight = (id: number): number => (id === FARMLAND_ID ? 3 : id === FARMLAND_DRY_ID ? 1 : 0);

/**
 * Vanilla getGrowthSpeed: `center` = weight of the farmland under the crop,
 * `around` = summed weights of the 8 cells around it (each 0, 1 or 3).
 */
export function growthSpeed(center: number, around: number): number {
  return 1 + center + around / 4;
}

/** Bone meal on a crop (vanilla getBonemealAgeIncrease 2..5; beetroots a third of that). */
export function boneMealStages(id: number, rnd: () => number): number {
  const c = BY_BLOCK.get(id);
  if (!c) return 0;
  const n = 2 + Math.floor(rnd() * 4);
  return c.fam.kind === 'beetroots' ? Math.floor(n / 3) : n;
}

/** Advance a crop by `n` ages (capped at mature). */
export function advance(id: number, n: number): number {
  const c = BY_BLOCK.get(id);
  if (!c) return id;
  return c.fam.stages[Math.min(c.fam.stages.length - 1, c.age + n)];
}

/** Chance one random tick ages a crop with growth speed f. */
export const growChance = (f: number): number => (f <= 0 ? 0 : 1 / (Math.floor(25 / f) + 1));

/** Next age's block id, or 0 when mature / not a crop. */
export function nextStage(id: number): number {
  const c = BY_BLOCK.get(id);
  if (!c || c.age >= c.fam.stages.length - 1) return 0;
  return c.fam.stages[c.age + 1];
}

/** binomial(n, p) */
function binom(n: number, p: number, rnd: () => number): number {
  let k = 0;
  for (let i = 0; i < n; i++) if (rnd() < p) k++;
  return k;
}

/**
 * Loot of a broken crop (vanilla loot tables; fortune = apply_bonus
 * binomial_with_bonus_count extra 3, p 0.5714, + fortune extra trials).
 */
export function cropDrops(id: number, rnd: () => number, fortune = 0): Stack[] {
  const c = BY_BLOCK.get(id);
  if (!c) return [];
  const mature = c.age === c.fam.stages.length - 1;
  const bonus = (): number => binom(3 + fortune, 0.5714, rnd);
  switch (c.fam.kind) {
    case 'wheat':
      return mature ? [stackOf(I.WHEAT), stackOf(I.WHEAT_SEEDS, 1 + bonus())] : [stackOf(I.WHEAT_SEEDS)];
    case 'carrots':
      return [stackOf(I.CARROT, mature ? 1 + bonus() : 1)];
    case 'potatoes': {
      const out = [stackOf(I.POTATO, mature ? 1 + bonus() : 1)];
      if (mature && rnd() < 0.02) out.push(stackOf(I.POISONOUS_POTATO));
      return out;
    }
    case 'beetroots':
      return mature ? [stackOf(I.BEETROOT), stackOf(I.BEETROOT_SEEDS, 1 + bonus())] : [stackOf(I.BEETROOT_SEEDS)];
  }
}
